import { SubcategoryDetailProps } from "../constants/settingsStructure";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import { SwivuList } from "@/modern/components/swivu-list";
import { useSwivuStore } from "@/react/hooks/useSwivuStore";
import { useState, useEffect, useMemo } from "react";
import { ResourceType } from "@/bryntum-lib/models/ResourceType";
import { toast } from "sonner";
import { GRANTS } from "@/other/constants/PrivilegeTypes";
import { STORE_KEYS } from "@/other/constants/StoreKeys";

const ResourceTypeDetail = ({ subcategory }: SubcategoryDetailProps) => {
  const { project, user } = useSwivuBryntumContext();
  const [types, setTypes] = useState<ResourceType[]>([]);

  const canEdit = useMemo<boolean>(() => {
    if (!user) return false;
    return user.can(GRANTS.grant_one);
  }, [user])

  const { subscribe } = useSwivuStore({
    storeId: STORE_KEYS.resourceTypeStore,
  })

  const loadTypes = () => {
    if (!project) return;
    const typeStore = project.resourceTypeStore;
    const typeRecords = typeStore.query(() => true, true) || [];
    setTypes(() => typeRecords);
  }

  useEffect(() => {
    loadTypes();
    return subscribe(() => {
      loadTypes();
    })
  }, [subscribe]);

  const isDuplicateTypeName = (name: string, excludeTypeId?: string | number) => {
    if (!project) return false;
    const typeStore = project.resourceTypeStore;
    const allTypes = typeStore.query(() => true, true) || [];
    return allTypes.some(t => {
      if (excludeTypeId !== undefined && t.id === excludeTypeId) {
        return false;
      }
      return t.description.toLowerCase() === name.toLowerCase();
    });
  };

  const handleAddItem = (value: string) => {
    if (!value.trim()) {
      return;
    }

    if (isDuplicateTypeName(value)) {
      toast.error('Resource type name already exists');
      return;
    }

    if (!project) return;
    const typeStore = project.resourceTypeStore;
    const newType = typeStore.createRecord({
      description: value,
    });
    typeStore.add(newType);
  }

  const handleReorderItems = (movedItem: ResourceType, beforeItem: ResourceType | null) => {
    if (!project) return;
    const typeStore = project.resourceTypeStore;
    // @ts-expect-error - ResourceType is a ResourceType model
    typeStore.move(movedItem, beforeItem);
  }

  const handleUpdateTypeName = (type: ResourceType, newName: string) => {
    if (!newName.trim()) {
      return false;
    }

    if (isDuplicateTypeName(newName, type.id)) {
      toast.error('Resource type name already exists');
      return false;
    }

    type.description = newName;
    return true;
  }

  const handleDeleteType = (type: ResourceType) => {
    if (!project) return;
    const typeStore = project.resourceTypeStore;
    // @ts-expect-error - type is a ResourceType model
    typeStore.remove(type);
  }

  return (
    <div className="p-6 flex flex-col max-w-3xl w-full h-full">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-foreground mb-2">{subcategory.label}</h2>
        <p className="text-sm text-muted-foreground">
          {subcategory.description}
        </p>
      </div>

      <SwivuList.Root<ResourceType>
        items={types}
        onReorder={handleReorderItems}
        searchConfig={{
          fields: ["description"],
          placeholder: "Search resource types...",
        }}
      >
        <SwivuList.Toolbar />

        <SwivuList.Content<ResourceType> emptyMessage="No resource types yet. Create your first resource type!">
          {(type: ResourceType) => (
            <SwivuList.Item value={type.id as string | number}>
              {/* Drag Handle */}
              <SwivuList.ItemHandle disabled={!canEdit} value={type.id as string | number} />

              {/* Resource Type Name */}
              <div className="flex-1 min-w-0 flex items-center gap-2">
                <SwivuList.TextField
                  item={type}
                  value={type.description}
                  onUpdate={handleUpdateTypeName}
                  placeholder="Enter resource type"
                  disabled={!canEdit}
                  className="flex-1"
                />
              </div>

              {/* Actions */}
              <SwivuList.ItemActions
                disabled={!canEdit}
                onDelete={() => handleDeleteType(type)}
              />
            </SwivuList.Item>
          )}
        </SwivuList.Content>

        <SwivuList.QuickAdd
          disabled={!canEdit}
          onAdd={handleAddItem}
          placeholder="Add new resource type..."
        />
      </SwivuList.Root>
    </div>
  );
};

export default ResourceTypeDetail;
